import apiClient from "./apiClient";
import { cleanMediaUrl } from '@/utils/text';

const token = localStorage.getItem("accessToken");

const PRODUCT_ATTR_DEF_TTL_MS = 5 * 60 * 1000;
const productAttributeDefinitionsCache = new Map();

const invalidateProductsCache = () => {
  localStorage.removeItem('products');
  localStorage.removeItem('productsTimestamp');
  productAttributeDefinitionsCache.clear();
};

/**
 * Definiciones de atributos dinámicos por tipo (GET /product-attributes/?type=phone).
 * @param {string} productType
 * @param {{ force?: boolean }} [opts]
 * @returns {Promise<Array>}
 */
export const getProductAttributeDefinitions = async (productType, opts = {}) => {
  const type = (productType || 'accessory').trim().toLowerCase();
  const force = !!opts.force;
  if (!force) {
    const hit = productAttributeDefinitionsCache.get(type);
    if (hit && Date.now() - hit.t < PRODUCT_ATTR_DEF_TTL_MS) {
      return hit.data;
    }
  }
  const response = await apiClient.get('/product-attributes/', {
    params: { type },
    timeout: 12000,
  });
  const data = Array.isArray(response.data) ? response.data : [];
  productAttributeDefinitionsCache.set(type, { t: Date.now(), data });
  return data;
};

export const clearProductAttributeDefinitionsCache = () => {
  productAttributeDefinitionsCache.clear();
};

const refreshProductsCache = async () => {
  try {
    const response = await apiClient.get("/products/");
    localStorage.setItem('products', JSON.stringify(response.data));
    localStorage.setItem('productsTimestamp', Date.now().toString());
    return response.data;
  } catch (error) {
    console.error('Error refreshing products cache:', error);
    throw error;
  }
};

// Add enhanced retry logic helper
const retryRequest = async (fn, retries = 3, delay = 1000) => {
  try {
    return await fn();
  } catch (error) {
    // Only retry on timeout or network errors
    const isTimeout = error.code === 'ECONNABORTED' || 
                      error.message?.includes('timeout') || 
                      error.isTimeout || 
                      (error.originalError && (error.originalError.code === 'ECONNABORTED' || 
                                              error.originalError.message?.includes('timeout')));
                      
    const isNetworkError = error.message === 'Network Error' || 
                           error.isNetworkError || 
                           (error.originalError && error.originalError.message === 'Network Error');
    
    // Don't retry on auth errors, validation errors, etc.
    const isRetryable = isTimeout || isNetworkError;
    
    if (retries <= 0 || !isRetryable) {
      console.warn(`Not retrying request: ${retries <= 0 ? 'max retries reached' : 'error not retryable'}`, error);
      throw error;
    }
    
    console.info(`Retrying request after ${delay}ms, ${retries} retries left...`);
    await new Promise(resolve => setTimeout(resolve, delay));
    return retryRequest(fn, retries - 1, delay * 1.5);
  }
};

export const getProducts = async (params = {}, forceRefresh = false) => {  try {
    const token = localStorage.getItem('accessToken');
    // Try to get from cache first if not forcing refresh, no filters are active, and user is not authenticated
    const hasFilters = params.search || params.category_name || params.page > 1 || params.on_sale || params.brand;
    if (!token && !forceRefresh && !hasFilters) {
      const cached = localStorage.getItem('products');
      const timestamp = localStorage.getItem('productsTimestamp');
      if (cached && timestamp && (Date.now() - Number(timestamp)) < 300000) { // 5 min cache
        try {
          const data = JSON.parse(cached);
          if (data && data.results?.length > 0) {
            console.log('Serving products from cache.');
            return normalizeProductListPayload(data);
          }
        } catch (e) {
          console.warn('Invalid products cache. Clearing it.', e);
          localStorage.removeItem('products');
          localStorage.removeItem('productsTimestamp');
        }
      }
    }

    const queryParams = new URLSearchParams();
    
    // Handle pagination params
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size || 24);
    // Handle filter params
    if (params.search) queryParams.append('search', params.search);
    if (params.category_name) queryParams.append('category_name', params.category_name);
    if (params.brand) queryParams.append('brand', params.brand);
    if (params.is_available !== undefined) queryParams.append('is_available', params.is_available);
    if (params.stock_filter) queryParams.append('stock_filter', params.stock_filter);
    if (params.price_filter) queryParams.append('price_filter', params.price_filter);
    if (params.on_sale) queryParams.append('on_sale', 'true');
    if (params.ordering) queryParams.append('ordering', params.ordering);
    
    // Log for debugging
    console.log('API Request params:', params);
    console.log('Query string:', queryParams.toString());
    
    // Use optimized endpoint for category filtering
    let url;
    if (params.category_name && !params.on_sale) {
      url = `/products/by-category/?${queryParams.toString()}`;
      console.log('Using optimized category endpoint');
    } else {
      url = `/products/?${queryParams.toString()}`;
    }
    
    // Optimize timeout based on type of request
    const timeout = hasFilters ? 8000 : 15000; // Even shorter timeout for optimized category requests
    
    const response = await retryRequest(async () => {
      return await apiClient.get(url, { 
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(forceRefresh ? {'Cache-Control': 'no-cache'} : {})
        },
        timeout: timeout
      });
    }, hasFilters ? 2 : 3); // Fewer retries for filtered requests

    // Log API response for debugging
    console.log('API Response:', response.data);

    const formattedResponse = normalizeProductListPayload({
      results: response.data.results || [],
      count: response.data.count || 0,
      next: response.data.next,
      previous: response.data.previous
    });    // Cache successful response only for base requests
    if (!token && (!params.page || params.page === 1) && !params.search && !params.category_name && !params.on_sale && !params.brand) {
      localStorage.setItem('products', JSON.stringify(formattedResponse));
      localStorage.setItem('productsTimestamp', Date.now().toString());
    }

    return formattedResponse;

  } catch (error) {
    console.error('Error in getProducts:', error);
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      throw new Error('La solicitud tardó demasiado tiempo. Por favor, inténtalo nuevamente.');
    }
    throw error;
  }
}

const PRODUCT_SAVE_WITH_FILES_TIMEOUT_MS = 120000;

/** Asegura URLs absolutas de CDN (evita rutas relativas tipo /producto/fixglass.b-cdn.net/...). */
export function normalizeProductMedia(product) {
  if (!product || typeof product !== 'object') return product;
  const p = { ...product };
  if (p.image) p.image = cleanMediaUrl(p.image);
  if (p.image_2) p.image_2 = cleanMediaUrl(p.image_2);
  if (p.image_3) p.image_3 = cleanMediaUrl(p.image_3);
  if (p.image_4) p.image_4 = cleanMediaUrl(p.image_4);
  if (p.product_details && typeof p.product_details === 'object') {
    p.product_details = {
      ...p.product_details,
      image: p.product_details.image ? cleanMediaUrl(p.product_details.image) : p.product_details.image,
    };
  }
  return p;
}

function normalizeProductListPayload(data) {
  if (!data) return data;
  if (Array.isArray(data)) return data.map(normalizeProductMedia);
  if (Array.isArray(data.results)) {
    return { ...data, results: data.results.map(normalizeProductMedia) };
  }
  return normalizeProductMedia(data);
}

function formatProductSaveError(error) {
  const d = error.response?.data;
  if (!d) return error.message || 'Error al guardar el producto';
  if (typeof d === 'string') return d;
  if (d.message) return d.message;
  if (d.error) return typeof d.error === 'string' ? d.error : JSON.stringify(d.error);
  if (d.images) {
    const im = d.images;
    return Array.isArray(im) ? im.join(' ') : String(im);
  }
  if (d.payload) {
    const p = d.payload;
    return Array.isArray(p) ? p.join(' ') : String(p);
  }
  if (d.non_field_errors?.length) return d.non_field_errors.join(' ');
  if (d.attributes) {
    const a = d.attributes;
    return Array.isArray(a) ? a.join(' ') : String(a);
  }
  return 'Error al guardar el producto';
}

// Helper para detectar si un producto está en oferta
export const isProductOnSale = (product) => {
  // Verificar si el producto tiene descuento directo
  if (product.discount && parseFloat(product.discount) > 0) {
    return true;
  }
  
  // Verificar si el precio final es menor que el precio original
  if (product.final_price && product.price && parseFloat(product.final_price) < parseFloat(product.price)) {
    return true;
  }
  
  // Verificar si hay descuentos totales (que pueden venir de ofertas activas)
  if (product.total_discount && parseFloat(product.total_discount) > 0) {
    return true;
  }
  
  // Verificar si tiene promoción especial marcada
  if (product.has_special_promotion) {
    return true;
  }
  
  return false;
};

export const getProductsOnSale = async () => {
  try {
    const response = await apiClient.get('/products/on-sale/', {
      timeout: 15000 // Smaller timeout for on-sale products
    });
    // Verificar que la respuesta tenga la estructura correcta
    if (!response.data || !response.data.results) {
      console.warn('Unexpected API response format:', response);
      return { results: [] };
    }
    return normalizeProductListPayload(response.data);
  } catch (error) {
    console.error('Error fetching products on sale:', error);
    return { results: [] }; // Retornar objeto con array vacío en caso de error
  }
};

export const getLowStockNotifications = async ({ threshold = 10 } = {}) => {
  const response = await apiClient.get('/products/low-stock/', {
    params: { threshold },
    headers: {
      Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
    },
  });
  return response.data;
};

export const getProduct = async (id, forceRefresh = false) => {
  const idStr = id != null ? String(id).trim() : '';
  if (!/^\d+$/.test(idStr)) {
    const err = new Error('Identificador de producto no válido');
    err.status = 404;
    throw err;
  }
  try {
    // Use the retryRequest helper for better timeout handling
    const response = await retryRequest(async () => {
      return await apiClient.get(`/products/${idStr}/`, {
        timeout: 15000, // 15 second timeout
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(forceRefresh
            ? { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
            : { 'Cache-Control': 'max-age=300' } // 5 minute cache
          )
        },
        params: forceRefresh ? { t: Date.now() } : undefined
      });
    }, 3, 1000); // 3 retries with 1s, 1.5s, 2.25s delays
    
    if (!response.data) {
      throw new Error('No se encontró el producto');
    }
    
    return normalizeProductMedia(response.data);
  } catch (error) {
    console.error('Error fetching product:', error.response || error);
    
    // Handle timeout errors specifically
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout') || 
        (error.originalError && (error.originalError.code === 'ECONNABORTED' || 
         error.originalError.message?.includes('timeout')))) {
      throw new Error('La solicitud tardó demasiado tiempo. Por favor, inténtalo nuevamente.');
    }
    
    // Capturar específicamente errores 404 y otros errores de respuesta
    if (error.response) {
      const errorStatus = error.response.status;
      const errorData = error.response.data;
      
      // Incluir el status en el error para manejarlo en el componente
      const errorObj = new Error(
        errorData?.message || 
        errorData?.error || 
        'Error al obtener el producto'
      );
      errorObj.status = errorStatus;
      throw errorObj;
    }
    
    // Network or other errors
    throw new Error('Error de conexión al obtener el producto. Verifica tu conexión a internet.');
  }
};

/**
 * @param {{ imageFile?: File, image2File?: File, image3File?: File, image4File?: File }} imageFiles - Si hay archivos, el backend recibe multipart
 *   (payload JSON + image_file ... image_4_file) y sube vía API de medios en el servidor.
 */
export const updateProduct = async (productId, productData, imageFiles = {}) => {
  try {
    const dataToSend = {
      ...productData
    };

    if (productData.name !== undefined) {
      dataToSend.name = productData.name?.trim();
      dataToSend.brand = (productData.brand || '').trim();
      dataToSend.description = (productData.description || '').trim();
      dataToSend.description_marVerde = (productData.description_marVerde || '').trim();
      dataToSend.category = productData.category || '';
      dataToSend.volume = (productData.volume || '').trim();
      dataToSend.dimensions = (productData.dimensions || '').trim();
      dataToSend.image = productData.image || null;
      dataToSend.image_2 = productData.image_2 || null;
      dataToSend.image_3 = productData.image_3 || null;
      dataToSend.image_4 = productData.image_4 || null;
      dataToSend.features = Array.isArray(productData.features) ? productData.features : [];
      dataToSend.is_available = Boolean(productData.is_available);
      dataToSend.is_available_marVerde = Boolean(productData.is_available_marVerde);
    }

    if (productData.price !== undefined) {
      dataToSend.price = Number(productData.price);
    }
    if (productData.stock !== undefined) {
      dataToSend.stock = Number(productData.stock);
    }
    if (productData.discount !== undefined) {
      dataToSend.discount = Number(productData.discount);
    }

    const { imageFile, image2File, image3File, image4File } = imageFiles;
    const hasFiles = !!(imageFile || image2File || image3File || image4File);

    const response = hasFiles
      ? await (() => {
          const fd = new FormData();
          fd.append('payload', JSON.stringify(dataToSend));
          if (imageFile) fd.append('image_file', imageFile);
          if (image2File) fd.append('image_2_file', image2File);
          if (image3File) fd.append('image_3_file', image3File);
          if (image4File) fd.append('image_4_file', image4File);
          return apiClient.patch(`/products/${productId}/`, fd, {
            timeout: PRODUCT_SAVE_WITH_FILES_TIMEOUT_MS,
          });
        })()
      : await apiClient.patch(`/products/${productId}/`, dataToSend, {
          headers: { 'Content-Type': 'application/json' },
        });

    invalidateProductsCache();
    refreshProductsCache();

    return normalizeProductMedia(response.data);
  } catch (error) {
    console.error('Error updating product:', error.response || error);
    throw new Error(formatProductSaveError(error));
  }
};

export const createProduct = async (productData, imageFiles = {}) => {
  try {
    console.log('Creando producto:', productData);
    const { imageFile, image2File, image3File, image4File } = imageFiles;
    const hasFiles = !!(imageFile || image2File || image3File || image4File);

    const response = hasFiles
      ? await (() => {
          const fd = new FormData();
          fd.append('payload', JSON.stringify(productData));
          if (imageFile) fd.append('image_file', imageFile);
          if (image2File) fd.append('image_2_file', image2File);
          if (image3File) fd.append('image_3_file', image3File);
          if (image4File) fd.append('image_4_file', image4File);
          return apiClient.post('/products/', fd, {
            timeout: PRODUCT_SAVE_WITH_FILES_TIMEOUT_MS,
          });
        })()
      : await apiClient.post('/products/', productData, {
          headers: { 'Content-Type': 'application/json' },
        });

    invalidateProductsCache();
    await refreshProductsCache();
    return normalizeProductMedia(response.data);
  } catch (error) {
    throw new Error(formatProductSaveError(error));
  }
};

export const deleteProduct = async (productId) => {
  try {
    const token = localStorage.getItem("accessToken");
    const response = await apiClient.delete(`/products/${productId}/`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      }
    });
    
    // Invalidar caché al eliminar
    invalidateProductsCache();
    
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Error al eliminar el producto');
  }
};

export const getCategories = async () => {
  try {
    const token = localStorage.getItem("accessToken");
    const response = await apiClient.get('/products/categories/', {
      headers: {
        Authorization: `Bearer ${token}`,
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching categories:', error);
    throw new Error('Error al cargar las categorías');
  }
};

// New function to create a category
export const createCategory = async (categoryData) => {
  try {
    const token = localStorage.getItem("accessToken");
    // Make sure we're using the correct endpoint
    const response = await apiClient.post('/categories/', categoryData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error creating category:', error);
    throw new Error(error.response?.data?.message || error.response?.data?.error || 'Error al crear la categoría');
  }
};

// New function to delete a category
export const generateProductSku = async (name, categoryName = '', variantSuffix = '') => {
  try {
    const token = localStorage.getItem('accessToken');
    const params = { name };
    if (categoryName) params.category_name = categoryName;
    if (variantSuffix) params.variant_suffix = variantSuffix;
    const response = await apiClient.get('/products/generate-sku/', {
      params,
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data.sku;
  } catch (error) {
    console.error('Error generating SKU:', error);
    throw new Error(error.response?.data?.error || 'Error al generar SKU');
  }
};

export const deleteCategory = async (categoryId) => {
  try {
    const token = localStorage.getItem("accessToken");
    const response = await apiClient.delete(`/categories/${categoryId}/`, {
      headers: {
        Authorization: `Bearer ${token}`,
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting category:', error);
    throw new Error(
      error.response?.data?.error || 
      error.response?.data?.message || 
      'Error al eliminar la categoría'
    );
  }
};

export const getRelatedProducts = async (productId) => {
  try {
    // Usar un endpoint más rápido para productos relacionados
    const response = await apiClient.get(`/products/${productId}/related/`, {
      timeout: 20000, // Aumentado a 20 segundos para productos relacionados
      headers: {
        'Cache-Control': 'max-age=300' // Cache de 5 minutos en el navegador
      }
    });
    
    console.log('Raw API response for related products:', response);
    
    // Verificar que la respuesta tenga la estructura correcta
    if (!response.data) {
      console.warn('No data in API response for related products');
      return { results: [] };
    }
    
    // Manejar tanto la estructura con results como el array directo
    if (response.data.results) {
      console.log('Using results structure from API');
      return response.data;
    } else if (Array.isArray(response.data)) {
      console.log('Converting array response to results structure');
      return {
        count: response.data.length,
        next: null,
        previous: null,
        results: response.data
      };
    } else {
      console.warn('Unexpected API response format for related products:', response.data);
      return { results: [] };
    }
    
  } catch (error) {
    console.error('Error fetching related products:', error);
    
    // En caso de timeout o error de red, retornar array vacío rápidamente
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      console.warn('Related products request timed out, returning empty array');
      return { results: [] };
    }
    
    if (error.message === 'Network Error') {
      console.warn('Network error for related products, returning empty array');
      return { results: [] };
    }
    
    return { results: [] }; // Retornar objeto con array vacío en caso de error
  }
};

export const getSearchSuggestions = async (query) => {
  try {
    if (!query || query.length < 2) return { results: [] };
    
    // Enviar el query directamente - el backend ahora maneja la tokenización
    // Usar timeout corto para sugerencias rápidas
    const response = await apiClient.get(`/products/?search=${encodeURIComponent(query)}&page_size=6`, {
      timeout: 5000 // Timeout más corto para sugerencias
    });
    
    return { results: response.data?.results || [] };
  } catch (error) {
    console.error('Error fetching suggestions:', error);
    return { results: [] };
  }
};

// === PRODUCT REVIEWS ===

// Obtener reseñas para un producto específico
export const getProductReviews = async (productId) => {
  try {
    const response = await apiClient.get(`/reviews/for_product/?product_id=${productId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching product reviews:', error);
    return [];
  }
};

// Crear una nueva reseña
export const createProductReview = async (reviewData) => {
  try {
    const token = localStorage.getItem("accessToken");
    const response = await apiClient.post("/reviews/", reviewData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error creating review:', error);
    throw new Error(
      error.response?.data?.error || 
      error.response?.data?.message || 
      'Error al crear la reseña'
    );
  }
};

// Verificar si el usuario ya ha dejado una reseña para un producto
export const hasUserReviewed = async (productId) => {
  try {
    const token = localStorage.getItem("accessToken");
    if (!token) return { exists: false };
    
    const response = await apiClient.get(`/reviews/has_reviewed/?product_id=${productId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error checking user review:', error);
    return { exists: false };
  }
};

// Obtener reseñas del usuario actual
export const getUserReviews = async () => {
  try {
    const token = localStorage.getItem("accessToken");
    const response = await apiClient.get("/reviews/my_reviews/", {
      headers: {
        Authorization: `Bearer ${token}`,
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching user reviews:', error);
    throw new Error('Error al obtener las reseñas del usuario');
  }
};

// === ADMIN FUNCTIONS ===

// Obtener reseñas pendientes de aprobación
export const getPendingReviews = async () => {
  try {
    const token = localStorage.getItem("accessToken");
    const response = await apiClient.get("/reviews/pending/", {
      headers: {
        Authorization: `Bearer ${token}`,
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching pending reviews:', error);
    throw new Error('Error al obtener las reseñas pendientes');
  }
};

// Aprobar una reseña
export const approveReview = async (reviewId) => {
  try {
    const token = localStorage.getItem("accessToken");
    const response = await apiClient.patch(`/reviews/${reviewId}/approve/`, {}, {
      headers: {
        Authorization: `Bearer ${token}`,
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error approving review:', error);
    throw new Error('Error al aprobar la reseña');
  }
};

// Rechazar una reseña
export const rejectReview = async (reviewId) => {
  try {
    const token = localStorage.getItem("accessToken");
    const response = await apiClient.patch(`/reviews/${reviewId}/reject/`, {}, {
      headers: {
        Authorization: `Bearer ${token}`,
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error rejecting review:', error);
    throw new Error('Error al rechazar la reseña');
  }
};

// Eliminar una reseña
export const deleteReview = async (reviewId) => {
  try {
    const token = localStorage.getItem("accessToken");
    const response = await apiClient.delete(`/reviews/${reviewId}/`, {
      headers: {
        Authorization: `Bearer ${token}`,
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting review:', error);
    throw new Error('Error al eliminar la reseña');
  }
};
// Alias para deleteReview (compatibilidad con componentes que usan este nombre)
export const deleteProductReview = deleteReview;

// Obtener todas las reseñas (admin)
export const getAllReviews = async () => {
  try {
    const token = localStorage.getItem("accessToken");
    const response = await apiClient.get("/reviews/", {
      headers: {
        Authorization: `Bearer ${token}`,
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching all reviews:', error);
    throw new Error('Error al obtener todas las reseñas');
  }
};
